import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaArrowRight } from "react-icons/fa";
import centerIcon from "../../assets/other/Link.svg";
import { fetchTrendingThunk } from "../../redux/actions/newArrivalActions";
import { Link, useNavigate } from "react-router-dom";
import * as other from "../other";
import { addToCartThunk } from "../../redux/actions/cartActions";
import { toast } from "react-toastify";
import { checkStockAvailability } from "../../redux/actions/stockActions";

const MainHome = () => {
  const dispatch = useDispatch();

  const {
    items: products,
    loading,
    error,
  } = useSelector((state) => state.newArrivals);
  const userType = useSelector((state) => state.auth?.user?.user?.user_type);
  const { isAuthenticated } = useSelector((state) => state.auth);

  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subProducts, setSubProducts] = useState([]);

  // New: quantities state for modal sub-products
  const [quantities, setQuantities] = useState({});
  const [slideInterval, setSlideInterval] = useState(null);
  const [resumeTimeout, setResumeTimeout] = useState(null);

  useEffect(() => {
    dispatch(fetchTrendingThunk());
  }, [dispatch]);

  useEffect(() => {
    if (products.length && !isModalOpen) {
      const interval = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % products.length);
      }, 5000);
      setSlideInterval(interval);
      return () => clearInterval(interval);
    }
  }, [products, isModalOpen]);

  const nextSlide = () => {
    // Stop the auto-slide interval
    if (slideInterval) {
      clearInterval(slideInterval);
      setSlideInterval(null);
    }

    // Clear any existing timeout for resuming auto-slide
    if (resumeTimeout) {
      clearTimeout(resumeTimeout);
    }

    // Navigate to the next slide
    setActiveIndex((prev) => (prev + 1) % products.length);

    // Set a timeout to resume auto-slide after 5 seconds of inactivity
    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % products.length);
      }, 3000);
      setSlideInterval(interval);
    }, 3000); // Adjust the delay as needed
    setResumeTimeout(timeout);
  };

  const currentProduct = products[activeIndex];
  const nextProduct = products[(activeIndex + 1) % products.length];

  const handleAddToCartClick = () => {
    if (currentProduct?.SubProducts?.length) {
      setSubProducts(currentProduct.SubProducts);

      // Reset quantities when opening modal
      const initialQuantities = {};
      currentProduct.SubProducts.forEach((sub) => {
        initialQuantities[sub.sub_pro_id] = 1;
      });
      setQuantities(initialQuantities);

      setIsModalOpen(true);
    } else {
      toast.info("No sub-products available for this item.");
    }
  };

  // Quantity controls handlers
  // const incrementQuantity = (sub) => {
  //   let temp = { ...quantities };
  //   let qty = (userType === 'vendor') ? (parseInt(temp[sub.sub_pro_id]) + 1) * parseInt(sub.no_of_pcs) : (parseInt(temp[sub.sub_pro_id]) + 1);

  //   if (qty > parseInt(sub.no_of_stock)) {
  //     toast.info("Insufficient stock");
  //   } else {
  //     setQuantities(prev => ({
  //       ...prev,
  //       [sub.sub_pro_id]: (prev[sub.sub_pro_id] || 1) + 1,
  //     }));
  //   }
  // };

  const incrementQuantity = (sub) => {
    const sub_product_id = sub.sub_pro_id;
    const quantity = (quantities[sub_product_id] || 1) + 1;

    // First, log what we're sending

    dispatch(checkStockAvailability(sub_product_id, quantity))
      .then((response) => {
        // Log the entire response to see its structure

        // The response structure is likely different than expected
        // The data might be directly in response.data, not in response.payload.data
        const data = response.data || {};

        // Check if there's stock available
        if (
          data.data?.data?.status === "out_of_stock" ||
          data.data?.available_stock < data.data?.required_quantity
        ) {
          toast.info("Insufficient Stock !!!");
        } else {
          // Stock is available, increment quantity
          setQuantities((prev) => ({
            ...prev,
            [sub_product_id]: quantity,
          }));
        }
      })
      .catch((error) => {
        // Log the error for debugging

        // Display a more specific error message if available
        const errorMessage =
          error.message || "Failed to check stock. Please try again.";
        toast.error(errorMessage);
      });
  };

  const decrementQuantity = (id) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: prev[id] > 1 ? prev[id] - 1 : 1,
    }));
  };

  // Add sub-product with selected quantity to cart
  const handleAddSubProductToCart = (sub) => {
    const product_id = currentProduct.pro_id;
    const sub_product_id = sub.sub_pro_id;
    const quantity = quantities[sub_product_id] || 1;

    dispatch(addToCartThunk({ product_id, sub_product_id, quantity }));
  };

  if (loading)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "75vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="alert alert-danger text-center mt-5" role="alert">
        {error}
      </div>
    );

  if (!products.length) return null;

  return (
    <section className="container overflow-hidden relative">
      <div className="flex flex-col-reverse lg:flex-row gap-5">
        {/* Left Column: Text */}
        <div className="col-lg-6">
          <div className="flex flex-col items-start">
            <h1 className="font-bold text-4xl mt-3">
              {currentProduct.product_name} <br />
              <span className="text-2xl">
                {currentProduct.product_sub_title}
              </span>
            </h1>
            <p className="mt-4">{currentProduct.product_description}</p>

            <div className="d-flex mt-3 mb-5">
              <button
                onClick={handleAddToCartClick}
                className="btn theme-btn px-4 mr-3"
              >
                ADD TO CART
              </button>
              <Link
                to={`/ProductsDetails/${currentProduct.pro_id}`}
                className="btn theme-outline-btn px-4"
              >
                VIEW DETAIL
              </Link>
            </div>
            <div className="w-full flex justify-between items-center">
              <div className="d-none d-lg-flex align-items-start">
                <p className="text-9xl font-bold">*</p>
                <p className="font-semibold mt-3">
                  Trendy and Classic <br /> for the New Season
                </p>
                {/* <div className="text-9xl font-bold">*</div>
                <div className="ml-4">
                  <small className="font-semibold text-4xl">
                    {currentProduct.tag_name}
                  </small>
                  <p className="mt-3 font-semibold uppercase">
                    {currentProduct.sub_cat_name}
                  </p>
                </div> */}
              </div>
              {/* Rotating Icon */}
              <div className="hidden lg:flex justify-center">
                <button className="rotate-button">
                  <img
                    loading="lazy"
                    src={centerIcon}
                    alt="center icon"
                    className="h-40"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Image + Next Image Preview */}
        <div className="relative col-lg-6 self-stretch">
          <div className="flex gap-5 mt-2 justify-between items-center">
            {/* Main Image */}
            {currentProduct?.product_primary_img && (
              <div className="w-full shrink-0 md:w-[389px] h-[519px] relative rounded-lg">
                <img
                  loading="lazy"
                  src={currentProduct?.product_primary_img}
                  alt={currentProduct.product_name}
                  className="size-full rounded-xl shadow-lg object-cover"
                />
                {/* Next Arrow Button */}
                <div className="z-50 absolute size-[15vw] md:size-[8vw] lg:size-[6vw] bottom-4 right-4 lg:bottom-20 lg:-right-12 cursor-pointer">
                  <button
                    className="btn btn-dark xl:text-2xl flex justify-center items-center rounded-full size-full border-[2vw] lg:border-[1.5vw]  border-white"
                    onClick={nextSlide}
                  >
                    <FaArrowRight />
                  </button>
                </div>
              </div>
            )}

            {/* Next Product Image Preview */}
            {products.length > 1 && (
              <div className="relative shrink-0 w-[250px] h-[450px] ml-10 rounded-xl overflow-visible shadow-md">
                {nextProduct?.product_primary_img && (
                  <div className="size-full">
                    <img
                      loading="lazy"
                      src={nextProduct.product_primary_img}
                      alt={nextProduct.product_name}
                      className="w-full h-full object-cover opacity-90 hover:opacity-100 transition"
                    />
                  </div>
                )}
                <div className="absolute inset-y-0 left-0 flex items-center justify-center -translate-x-1/2">
                  <span
                    className="text-transparent text-white text-7xl font-extrabold tracking-wider select-none"
                    style={{
                      WebkitTextStroke: "1.5px black",
                      transform: "rotate(-90deg)",
                      whiteSpace: "nowrap",
                      userSelect: "none",
                      textShadow: "1px 1px 2px rgba(0,0,0,0)",
                      zIndex: 20,
                    }}
                  >
                    SUMMER
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for SubProducts */}
      <other.Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Select Sub Product"
      >
        <div className="flex flex-col gap-4">
          {subProducts.map((sub, idx) => (
            <div
              key={idx}
              className="w-full border rounded-xl p-4 shadow hover:shadow-lg transition flex flex-row items-center gap-4"
            >
              {/* Image on the left */}
              <img
                loading="lazy"
                src={sub.sub_pro_img}
                alt={`sub-product-${idx}`}
                className="w-32 h-44 object-cover hover:scale-110 transition-transform ease-in-out duration-500 cursor-pointer rounded-lg flex-shrink-0"
              />

              {/* Details on the right */}
              <div className="flex flex-col justify-between flex-grow h-full">
                <div className="space-y-1">
                  <p>
                    <strong>Color:</strong> {sub.color_id}
                  </p>
                  <p>
                    <strong>Size:</strong> {sub.size}
                  </p>
                  {/* <p><strong>Location:</strong> {sub.location}</p> */}
                  {isAuthenticated ? (
                    <p>
                      <strong>Price:</strong> ₹
                      {userType === "vendor"
                        ? sub.set_price
                        : sub.customer_product_price}
                    </p>
                  ) : (
                    ""
                  )}
                </div>

                {/* Quantity controls */}
                <div className="self-start flex items-center mt-2 gap-2 ">
                  <div className="border border-gray-300 rounded-full overflow-hidden">
                    <button
                      onClick={() => decrementQuantity(sub.sub_pro_id)}
                      className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
                      disabled={quantities[sub.sub_pro_id] <= 1}
                    >
                      -
                    </button>
                    <span className="px-4 py-1 select-none">
                      {quantities[sub.sub_pro_id] || 1}
                    </span>
                    <button
                      onClick={() => incrementQuantity(sub)}
                      className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
                    >
                      +
                    </button>
                  </div>

                  {userType === "vendor" ? (
                    sub.selling_type == "set" ? (
                      <span className="ml-2">
                        {quantities[sub.sub_pro_id] <= 1 ? "Set" : "Sets"}{" "}
                      </span>
                    ) : (
                      <span className="ml-2">
                        {quantities[sub.sub_pro_id] <= 1 ? "Piece" : "Pieces"}
                      </span>
                    )
                  ) : null}
                </div>

                <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                  {/* Note Text */}
                  {userType === "vendor" &&
                  sub.selling_type == "set" &&
                  sub.no_of_pcs ? (
                    <p className="italic text-sm text-red-600 font-semibold whitespace-nowrap min-w-[180px]">
                      {` ${quantities[sub.sub_pro_id] || 1} Set = ${
                        sub.no_of_pcs * (quantities[sub.sub_pro_id] || 1)
                      } pieces`}{" "}
                      (₹{sub.set_price * (quantities[sub?.sub_pro_id] || 1)})
                    </p>
                  ) : (
                    // Invisible div to take up the same space so button stays right-aligned
                    <div className="min-w-[180px]" />
                  )}

                  {/* Add Button */}
                  {isAuthenticated ? (
                    <div className="flex flex-col gap-1 w-full sm:w-fit">
                      <button
                        className="btn theme-outline-btn disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleAddSubProductToCart(sub)}
                        disabled={sub.no_of_stock <= 0}
                      >
                        Add Item
                      </button>

                      {sub.no_of_stock <= 0 && (
                        <p className="text-red-600 text-sm font-semibold">
                          Out of Stock
                        </p>
                      )}
                    </div>
                  ) : (
                    navigate("/login")
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </other.Modal>
    </section>
  );
};

export default MainHome;
